// Clean, app-ready digital "card skins" — bank-accurate premium artwork rendered
// in pure CSS/SVG (no photo crops, no trademarked raster logos). Each real card
// product gets a recognisable identity (colours, motif, chip, network mark) that
// is auto-matched from issuer_bank + card_name. Purely presentational — no finance
// logic, no PAN. When a card has uploaded artwork that takes precedence in the UI.

const norm = (s) => (s || "").toString().toLowerCase();

// EMV chip graphic (gold or silver).
function Chip({ tone = "gold", className = "" }) {
  const grad = tone === "silver"
    ? "linear-gradient(135deg,#eef1f5,#c3cbd6 45%,#9aa6b4)"
    : "linear-gradient(135deg,#f8ecb6,#d9b451 45%,#a9812f)";
  return (
    <div className={`relative rounded-[3px] ${className}`} style={{ background: grad, width: "13%", aspectRatio: "1.32/1" }}>
      <div className="absolute inset-0 opacity-60" style={{
        backgroundImage:
          "linear-gradient(90deg, rgba(0,0,0,.25) 1px, transparent 1px), linear-gradient(0deg, rgba(0,0,0,.18) 1px, transparent 1px)",
        backgroundSize: "33% 50%",
      }} />
      <div className="absolute inset-y-[18%] left-1/2 w-[1px] -translate-x-1/2 bg-black/25" />
    </div>
  );
}

// Small network marks (simplified, brand-adjacent — not the official logos).
export function NetworkMark({ network, tone = "light" }) {
  const n = norm(network);
  const light = tone === "light";
  if (n === "mastercard")
    return (
      <div className="flex items-center" title="Mastercard">
        <span className="block h-4 w-4 rounded-full bg-[#eb001b]" />
        <span className="block h-4 w-4 rounded-full bg-[#f79e1b] -ml-2 mix-blend-multiply" />
      </div>
    );
  if (n === "visa")
    return (
      <span className="italic font-extrabold tracking-tight text-[15px]" style={{ color: light ? "#fff" : "#1a1f71", textShadow: light ? "0 1px 2px rgba(0,0,0,.4)" : "none" }}>
        VISA
      </span>
    );
  if (n === "amex")
    return (
      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm tracking-wide" style={{ background: "#2e77bb", color: "#fff" }}>
        AMEX
      </span>
    );
  if (n === "diners")
    return (
      <div className="flex items-center gap-1" title="Diners Club">
        <span className="relative block h-4 w-4 rounded-full overflow-hidden" style={{ background: "#004a98" }}>
          <span className="absolute inset-y-0 left-1/2 w-[46%] bg-white/90" />
        </span>
        <span className="text-[8px] font-semibold leading-none" style={{ color: light ? "#fff" : "#004a98" }}>Diners<br />Club</span>
      </div>
    );
  if (n === "rupay")
    return (
      <span className="text-[12px] font-extrabold italic">
        <span style={{ color: "#f47216" }}>Ru</span><span style={{ color: "#1a8f4a" }}>Pay</span>
      </span>
    );
  return null;
}

// ---- Skin registry -------------------------------------------------------
// bg: any CSS background value. text: 'light' | 'dark'. chip: 'gold' | 'silver'.
// motif: optional JSX rendered behind the identity layer.
const sheen = (a = ".18") =>
  <div className="absolute inset-0" style={{ background: `linear-gradient(115deg, transparent 40%, rgba(255,255,255,${a}) 50%, transparent 60%)` }} />;

const SKINS = [
  {
    key: "axis-burgundy",
    match: (b, n) => b.includes("axis") && (n.includes("burgundy") || n.includes("private") || n.includes("pvt")),
    bank: "AXIS BANK", tier: "BURGUNDY PRIVATE",
    bg: "linear-gradient(135deg,#111114 0%,#1c1a1e 50%,#0a0a0c 100%)", text: "light", chip: "gold",
    motif: (<><div className="absolute right-0 bottom-0 h-2/3 w-2/3" style={{ background: "radial-gradient(120% 120% at 100% 100%, #6e1830aa, transparent 60%)" }} /><div className="absolute inset-x-4 bottom-3 h-[2px]" style={{ background: "linear-gradient(90deg,transparent,#c8a24d99,transparent)" }} />{sheen(".08")}</>),
  },
  {
    key: "axis-magnus",
    match: (b, n) => b.includes("axis") && n.includes("magnus"),
    bank: "AXIS BANK", tier: "MAGNUS",
    bg: "linear-gradient(135deg,#1d1f24 0%,#2a2c33 55%,#141519 100%)", text: "light", chip: "gold",
    motif: (<><div className="absolute inset-0" style={{ background: "linear-gradient(120deg,transparent 45%, #b0308a55 60%, #6a2bd955 72%, transparent 85%)" }} /><div className="absolute right-4 top-4 h-16 w-16 rounded-full opacity-40" style={{ background: "conic-gradient(from 0deg,#e05aa8,#7a3ddf,#e05aa8)" }} />{sheen(".08")}</>),
  },
  {
    key: "axis",
    match: (b) => b.includes("axis"),
    bank: "AXIS BANK", tier: "",
    bg: "linear-gradient(135deg,#7a0f3d 0%,#97144d 55%,#5e0b2f 100%)", text: "light", chip: "gold",
    motif: sheen(".12"),
  },
  {
    key: "icici-emeralde",
    match: (b, n) => b.includes("icici") && n.includes("emerald"),
    bank: "ICICI BANK", tier: "EMERALDE",
    bg: "linear-gradient(135deg,#04352b 0%,#0a5a44 50%,#063a2e 100%)", text: "light", chip: "gold",
    motif: (<><div className="absolute inset-0 opacity-30" style={{ backgroundImage: "repeating-linear-gradient(45deg,#c9a24b22 0 2px,transparent 2px 10px)" }} /><div className="absolute -left-6 bottom-[-30px] h-36 w-36 rounded-full" style={{ background: "radial-gradient(circle,#d9b45144,transparent 70%)" }} />{sheen(".08")}</>),
  },
  {
    key: "icici-business",
    match: (b, n) => b.includes("icici") && (n.includes("business") || n.includes("corporate")),
    bank: "ICICI BANK", tier: "BUSINESS",
    bg: "linear-gradient(135deg,#8a6a12 0%,#d4a017 45%,#f2c94c 100%)", text: "dark", chip: "gold",
    motif: (<><div className="absolute inset-0 opacity-30" style={{ backgroundImage: "repeating-linear-gradient(60deg,#00000018 0 2px,transparent 2px 12px)" }} /><div className="absolute right-5 top-5 h-2 w-16 rounded-full" style={{ background: "#a12d5f" }} />{sheen(".14")}</>),
  },
  {
    key: "icici",
    match: (b) => b.includes("icici"),
    bank: "ICICI BANK", tier: "",
    bg: "linear-gradient(135deg,#8c1d40 0%,#c2541f 55%,#e2571f 100%)", text: "light", chip: "gold",
    motif: sheen(".12"),
  },
  {
    key: "hdfc-infinia",
    match: (b, n) => b.includes("hdfc") && n.includes("infinia"),
    bank: "HDFC BANK", tier: "INFINIA",
    bg: "linear-gradient(135deg,#08294f 0%,#0f4c8a 50%,#0a3160 100%)", text: "light", chip: "gold",
    motif: (<><div className="absolute inset-0 opacity-50" style={{ background: "linear-gradient(115deg, transparent 46%, #6fb2ff44 54%, transparent 62%)" }} /><div className="absolute -right-10 -top-10 h-40 w-40 rotate-12" style={{ background: "linear-gradient(135deg,#2b7fd455,transparent 70%)" }} /><div className="absolute inset-2 rounded-xl" style={{ border: "1px solid #cfe3ff44" }} />{sheen(".08")}</>),
  },
  {
    key: "hdfc-diners",
    match: (b, n) => (b.includes("hdfc") && n.includes("diners")) || (b.includes("diners")),
    bank: "HDFC BANK", tier: "DINERS CLUB",
    bg: "linear-gradient(135deg,#9aa1a8 0%,#c7ccd2 45%,#7f868d 100%)", text: "dark", chip: "silver",
    motif: (<><div className="absolute inset-0 opacity-40" style={{ background: "linear-gradient(105deg,transparent 52%, #ffffffaa 60%, transparent 68%)" }} /><div className="absolute right-6 bottom-6 h-14 w-14 rounded-full overflow-hidden opacity-90" style={{ background: "#0a1733" }}><span className="absolute inset-y-0 left-1/2 w-[46%] bg-white/90" /></div></>),
  },
  {
    key: "hdfc-easyshop",
    match: (b, n) => b.includes("hdfc") && (n.includes("easyshop") || n.includes("easy shop") || n.includes("platinum")),
    bank: "HDFC BANK", tier: "PLATINUM",
    bg: "linear-gradient(135deg,#274b6d 0%,#3f6f9c 50%,#22405d 100%)", text: "light", chip: "silver",
    motif: (<><div className="absolute inset-0 opacity-40" style={{ background: "linear-gradient(105deg,transparent 55%, #ffffff33 63%, transparent 71%)" }} /><div className="absolute left-4 top-4 h-2 w-14 rounded-full" style={{ background: "#e2231a" }} />{sheen(".1")}</>),
  },
  {
    key: "hdfc",
    match: (b) => b.includes("hdfc"),
    bank: "HDFC BANK", tier: "",
    bg: "linear-gradient(135deg,#003a73 0%,#004c8f 55%,#002a54 100%)", text: "light", chip: "gold",
    motif: (<><div className="absolute right-4 top-4 h-4 w-4" style={{ background: "#e2231a" }} />{sheen(".1")}</>),
  },
  {
    key: "hsbc",
    match: (b) => b.includes("hsbc"),
    bank: "HSBC", tier: "",
    bg: "linear-gradient(135deg,#f7f7f8 0%,#ffffff 60%,#eef0f2 100%)", text: "dark", chip: "gold",
    motif: (<><div className="absolute right-0 top-0 h-0 w-0" style={{ borderTop: "58px solid #db0011", borderLeft: "58px solid transparent" }} /><div className="absolute right-0 bottom-0 h-0 w-0" style={{ borderBottom: "58px solid #db0011", borderRight: "58px solid transparent" }} /></>),
  },
  {
    key: "thomascook",
    match: (b, n) => b.includes("thomas") || n.includes("enterprisefx") || n.includes("enterprise fx"),
    bank: "THOMAS COOK", tier: "ENTERPRISE FX",
    bg: "linear-gradient(135deg,#0a0a0c 0%,#17161a 55%,#050506 100%)", text: "light", chip: "gold",
    motif: (<><div className="absolute -right-6 -bottom-8 h-40 w-40 rounded-full opacity-70" style={{ background: "radial-gradient(circle,#ffd200aa,transparent 62%)" }} /><div className="absolute left-0 top-5 h-2.5 w-28" style={{ background: "#ffd200" }} /><div className="absolute left-4 top-9 text-[9px] font-extrabold tracking-wide" style={{ color: "#ffd200" }}>FX</div>{sheen(".07")}</>),
  },
  {
    key: "kotak",
    match: (b) => b.includes("kotak"),
    bank: "KOTAK", tier: "CORPORATE PLATINUM",
    bg: "linear-gradient(135deg,#0c0d10 0%,#26282e 52%,#08090b 100%)", text: "light", chip: "silver",
    motif: (<><div className="absolute inset-x-4 bottom-6 h-[3px]" style={{ background: "linear-gradient(90deg,transparent,#d7dbe0,transparent)" }} /><div className="absolute right-5 top-5 h-2 w-16 rounded-full" style={{ background: "#e2231a" }} /><div className="absolute inset-0 opacity-40" style={{ background: "linear-gradient(105deg,transparent 55%, #ffffff33 62%, transparent 70%)" }} /></>),
  },
  {
    key: "indusind",
    match: (b) => b.includes("indusind") || b.includes("indus ind"),
    bank: "INDUSIND BANK", tier: "NEXXT",
    bg: "linear-gradient(135deg,#0a2a63 0%,#1667c4 52%,#0a3a86 100%)", text: "light", chip: "gold",
    motif: (<><div className="absolute -left-8 -bottom-10 h-40 w-40 rounded-full opacity-40" style={{ background: "radial-gradient(circle,#8fc4ff55,transparent 65%)" }} /><div className="absolute right-5 top-5 h-2 w-14 rounded-full" style={{ background: "#e2231a" }} />{sheen(".1")}</>),
  },
  {
    key: "amex",
    match: (b) => b.includes("american express") || b.includes("amex"),
    bank: "AMERICAN EXPRESS", tier: "PLATINUM",
    bg: "linear-gradient(135deg,#8b939c 0%,#c6cdd4 45%,#7d858e 100%)", text: "dark", chip: "gold",
    motif: (<><div className="absolute inset-0 opacity-50" style={{ background: "linear-gradient(105deg,transparent 52%, #ffffff88 60%, transparent 68%)" }} /><div className="absolute right-5 bottom-5 h-10 w-10 rounded-sm" style={{ background: "#2e77bb" }} /></>),
  },
  {
    key: "sbi",
    match: (b) => b.includes("sbi") || b.includes("state bank"),
    bank: "SBI CARD", tier: "",
    bg: "linear-gradient(135deg,#0a2a6b 0%,#5b2a86 55%,#0a2352 100%)", text: "light", chip: "gold",
    motif: sheen(".12"),
  },
];

// Deterministic tasteful fallback (8 variants) when no product match.
const FALLBACKS = [
  "linear-gradient(135deg,#0f172a,#1e293b 55%,#312e81)",
  "linear-gradient(135deg,#0b132b,#1c2541 55%,#3a506b)",
  "linear-gradient(135deg,#0f2027,#203a43 55%,#2c5364)",
  "linear-gradient(135deg,#14213d,#1d3557 55%,#457b9d)",
  "linear-gradient(135deg,#1b1b2f,#162447 55%,#1f4068)",
  "linear-gradient(135deg,#232526,#2c3e50 55%,#414345)",
  "linear-gradient(135deg,#2b0a3d,#4a1052 55%,#7b2d6b)",
  "linear-gradient(135deg,#3a1c1c,#5c2b2b 55%,#8a3b3b)",
];

// Build a key → skin lookup so an exact last-4 mapping can pin a specific design.
const SKIN_BY_KEY = {};
SKINS.forEach((s) => { SKIN_BY_KEY[s.key] = s; });

// Exact last-4 → design (user-supplied mapping from the real-card reference photo).
// Primary matching key per the spec — guarantees no same-bank mix-ups.
const BY_LAST4 = {
  "6950": "axis-magnus",     // Axis Magnus
  "7864": "axis-burgundy",   // Axis Burgundy Private
  "4306": "icici-business",  // ICICI Business (gold)
  "7931": "hsbc",            // HSBC Business
  "2258": "hdfc-diners",     // HDFC Diners Club International (silver)
  "5194": "thomascook",      // Thomas Cook EnterpriseFX (black + yellow)
  "1001": "icici-emeralde",  // ICICI Emeralde (emerald)
  "0551": "hdfc-infinia",    // HDFC Infinia (blue angular)
  "7404": "indusind",        // IndusInd NEXXT / Platinum (blue)
  "1142": "kotak",           // Kotak Corporate Platinum (black/silver)
  "6217": "hdfc-easyshop",   // HDFC EasyShop Platinum
  "6362": "hdfc-easyshop",   // HDFC EasyShop Platinum (2nd)
};

export function resolveSkin(card) {
  const l4 = (card.last4 || (card.masked_number || "").replace(/\D/g, "").slice(-4) || "").slice(-4);
  if (l4 && BY_LAST4[l4] && SKIN_BY_KEY[BY_LAST4[l4]]) return SKIN_BY_KEY[BY_LAST4[l4]];
  const b = norm(card.issuer_bank);
  const n = norm(card.card_name);
  const found = SKINS.find((s) => {
    try { return s.match(b, n); } catch { return false; }
  });
  if (found) return found;
  const key = `${card.issuer_bank || ""}|${card.card_name || ""}`;
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return {
    key: "fallback",
    bank: (card.issuer_bank || "").toUpperCase(),
    tier: "",
    bg: FALLBACKS[h % FALLBACKS.length],
    text: "light",
    chip: "gold",
    motif: sheen(".1"),
    _fallback: true,
  };
}

// Real premium card artwork (cropped from the bank card gallery). Matched from
// issuer_bank + card_name so wallet tiles show the actual card face.
function realArtwork(card) {
  const s = norm(card.issuer_bank) + " " + norm(card.card_name) + " " + norm(card.card_type);
  const has = (...w) => w.every((x) => s.includes(x));
  if (has("axis", "magnus")) return "axis-magnus";
  if (has("axis", "burgundy")) return "axis-burgundy";
  if (has("axis") && (s.includes("world travel") || s.includes("travel"))) return "axis-world-travel";
  if (has("axis", "reward")) return "axis-reward";
  if (has("icici", "coral")) return "icici-coral";
  if (has("icici", "emirates")) return "icici-emirates-sapphiro";
  if (has("icici", "times")) return "icici-times-black";
  if (has("icici", "emerald")) return "icici-emerald";
  if (has("icici", "sapphiro")) return "icici-sapphiro";
  if (has("idfc", "select")) return "idfc-select";
  if (has("idfc") && (s.includes("club vistara") || s.includes("vistara"))) return "idfc-club-vistara";
  if (has("idfc", "mayura")) return "idfc-mayura";
  if (has("idfc", "indigo") && (s.includes("mastercard") || s.includes("master") || norm(card.network) === "mastercard")) return "idfc-indigo-mastercard";
  if (has("idfc", "indigo")) return "idfc-indigo-rupay";
  if (has("kotak", "solitaire")) return "kotak-solitaire";
  if (has("sbi", "ola")) return "sbi-ola";
  if ((s.includes("amex") || s.includes("american express")) && s.includes("platinum")) return "amex-platinum";
  if (s.includes("indusind") && s.includes("pinnacle")) return "indusind-pinnacle";
  if (s.includes("onecard") || s.includes("one card") || s.includes("bank of baroda") || (s.includes("bob") && s.includes("one"))) return "onecard-bob";
  return null;
}

// Full card-face artwork (identity only — no dynamic finance data).
// variant: 'tile' | 'detail'. The consumer overlays masked number / product / amounts.
export function CardSkin({ card, variant = "tile", className = "" }) {
  const art = card.artwork_url || (() => { const a = realArtwork(card); return a ? `/card-art/${a}.png` : null; })();
  if (art) {
    return (
      <div className={`absolute inset-0 overflow-hidden ${className}`} data-skin="photo" style={{ background: "#0b1220" }}>
        <img src={art} alt={card.card_name || "card"} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      </div>
    );
  }
  const skin = resolveSkin(card);
  const light = skin.text !== "dark";
  const ink = light ? "#ffffff" : "#0b1b3a";
  const bankSize = variant === "detail" ? "text-[13px]" : "text-[11px]";
  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`} style={{ background: skin.bg }} data-skin={skin.key}>
      {skin.motif}
      {/* identity layer */}
      <div className="absolute inset-0 p-[6%] flex flex-col">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <div className={`font-semibold tracking-wide ${bankSize}`} style={{ color: ink, textShadow: light ? "0 1px 2px rgba(0,0,0,.35)" : "none" }}>
              {skin.bank || (card.issuer_bank || "").toUpperCase()}
            </div>
            {skin.tier ? (
              <div className="text-[8px] font-medium tracking-[0.22em] mt-0.5" style={{ color: light ? "rgba(255,255,255,.72)" : "rgba(11,27,58,.6)" }}>
                {skin.tier}
              </div>
            ) : null}
          </div>
          <div className="shrink-0"><NetworkMark network={card.network} tone={light ? "light" : "dark"} /></div>
        </div>
        <div className="mt-[7%]"><Chip tone={skin.chip} /></div>
      </div>
    </div>
  );
}

export default CardSkin;
